import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronRight, ChevronLeft, Minus, Plus, Tag, CalendarDays, Clock, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, mediaUrl, fmtMoney, apiError, fmtHour } from "@/lib/api";

const STEPS = ["Your Details", "Service", "Date", "Time", "Add-ons", "Payment"];

const SMS_CONSENT_TEXT = "I agree to receive SMS text messages from XMD Productions (appointment confirmations, reminders, and updates) at the phone number provided. Message frequency varies. Message & data rates may apply. Reply STOP to unsubscribe or HELP for help.";

function fmtDate(d) {
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Book() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", dob: "", project_details: "", sms_consent: false });
  const [serviceId, setServiceId] = useState(params.get("service") || "");
  const [date, setDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [hours, setHours] = useState(1);
  const [equipment, setEquipment] = useState([]);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [booking, setBooking] = useState(null);
  const [paying, setPaying] = useState(false);

  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then((r) => r.data) });
  const { data: equip = [] } = useQuery({ queryKey: ["equipment"], queryFn: () => api.get("/equipment").then((r) => r.data) });
  const { data: avail } = useQuery({ queryKey: ["availability", serviceId], queryFn: () => api.get("/availability", { params: serviceId ? { service_id: serviceId } : {} }).then((r) => r.data) });
  const { data: studioHours } = useQuery({ queryKey: ["studio-hours"], queryFn: () => api.get("/studio-hours").then((r) => r.data) });
  const { data: payConfig } = useQuery({ queryKey: ["pay-config"], queryFn: () => api.get("/payments/config").then((r) => r.data) });

  const service = services.find((s) => s.id === serviceId);
  const dateStr = fmtDate(date);

  const { data: dayInfo, refetch: refetchDay } = useQuery({
    queryKey: ["day", dateStr, serviceId],
    queryFn: () => api.get("/availability/day", { params: { date: dateStr, ...(serviceId ? { service_id: serviceId } : {}) } }).then((r) => r.data),
    enabled: !!dateStr,
  });

  // Price (client preview; server is authoritative)
  const subtotalCents = useMemo(() => {
    let c = Math.round((service?.hourly_rate || 0) * hours * 100);
    equipment.forEach((id) => {
      const e = equip.find((x) => x.id === id);
      if (e) c += Math.round(e.price * 100);
    });
    return c;
  }, [service, hours, equipment, equip]);

  const discountCents = promo?.discount_cents || 0;
  const totalCents = Math.max(0, subtotalCents - discountCents);

  // Available start times for the chosen day
  const blockedDates = useMemo(() => new Set((avail?.blocked_dates || []).map((b) => b.date)), [avail]);
  const closedWeekdays = useMemo(() => {
    const h = studioHours?.hours || {};
    return new Set(Object.entries(h).filter(([, v]) => v.open === false).map(([k]) => Number(k)));
  }, [studioHours]);

  const availableStarts = useMemo(() => {
    if (!dayInfo || !dayInfo.open) return [];
    const booked = new Set([...(dayInfo.booked_hours || []), ...(dayInfo.blocked_hours || [])]);
    const starts = [];
    for (let h = dayInfo.start; h < dayInfo.end; h++) if (!booked.has(h)) starts.push(h);
    return starts;
  }, [dayInfo]);

  const maxHours = useMemo(() => {
    if (startTime == null || !dayInfo) return 1;
    const booked = new Set([...(dayInfo.booked_hours || []), ...(dayInfo.blocked_hours || [])]);
    let count = 0;
    for (let h = startTime; h < dayInfo.end; h++) {
      if (booked.has(h)) break;
      count++;
    }
    return Math.max(1, count);
  }, [startTime, dayInfo]);

  useEffect(() => { if (hours > maxHours) setHours(maxHours); }, [maxHours, hours]);
  useEffect(() => { setStartTime(null); setHours(1); }, [dateStr]);
  useEffect(() => { setPromo(null); }, [subtotalCents]);

  const toggleEquip = (id) => setEquipment((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    try {
      const { data } = await api.post("/promo/validate", { code: promoCode.trim(), subtotal_cents: subtotalCents });
      setPromo(data);
      toast.success(`Promo applied — you save ${fmtMoney(data.discount_cents)}`);
    } catch (e) {
      setPromo(null);
      toast.error(apiError(e, "Invalid promo code"));
    }
  };

  const canNext = () => {
    if (step === 0) return form.first_name && form.last_name && form.email && form.phone;
    if (step === 1) return !!serviceId;
    if (step === 2) return !!dateStr && dayInfo?.open;
    if (step === 3) return startTime != null;
    return true;
  };

  const createBooking = async () => {
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        dob: form.dob || null,
        service_id: serviceId,
        event_date: dateStr,
        start_time: startTime,
        hours,
        equipment,
        promo_code: promo?.code || null,
        sms_consent: !!form.sms_consent,
      });
      setBooking(data);
      return data;
    } catch (e) {
      if (!handleSlotTaken(e)) toast.error(apiError(e, "Could not create booking"));
      return null;
    }
  };

  const handleSlotTaken = (e) => {
    if (e?.response?.status !== 409) return false;
    toast.error(apiError(e), { duration: 8000 });
    setBooking(null);
    setStartTime(null);
    setPaying(false);
    setStep(3);
    refetchDay();
    return true;
  };

  const payWithStripe = async () => {
    setPaying(true);
    const b = booking || (await createBooking());
    if (!b) { setPaying(false); return; }
    try {
      const { data } = await api.post(`/bookings/${b.id}/stripe-checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      if (handleSlotTaken(e)) return;
      toast.error(apiError(e, "Payment could not start"));
      setPaying(false);
    }
  };

  const payWithPayPal = async () => {
    setPaying(true);
    const b = booking || (await createBooking());
    if (!b) { setPaying(false); return; }
    try {
      const { data } = await api.post(`/bookings/${b.id}/paypal-order`, { origin_url: window.location.origin });
      window.location.href = data.approve_url;
    } catch (e) {
      if (handleSlotTaken(e)) return;
      toast.error(apiError(e, "PayPal could not start"));
      setPaying(false);
    }
  };

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="pt-24 pb-28 max-w-3xl mx-auto px-5" data-testid="book-page">
      <p className="font-mono-label uppercase tracking-[0.3em] text-xs text-[#D4AF37]">Reserve Your Slot</p>
      <h1 className="font-display text-4xl md:text-5xl text-white mt-3 mb-6">Book a Session</h1>

      {/* Stepper */}
      <div className="flex items-center gap-1 mb-8 overflow-x-auto no-scrollbar">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center shrink-0">
            <div className={`flex items-center gap-2 ${i <= step ? "text-[#D4AF37]" : "text-white/35"}`}>
              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs border ${i < step ? "bg-[#D4AF37] border-[#D4AF37] text-black" : i === step ? "border-[#D4AF37]" : "border-white/20"}`}>
                {i < step ? <Check size={13} /> : i + 1}
              </span>
              <span className="text-xs font-mono-label uppercase tracking-wide hidden sm:block">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="w-4 sm:w-6 h-px bg-white/15 mx-1" />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
          {step === 0 && (
            <div className="space-y-4" data-testid="step-details">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name *"><Input data-testid="input-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-[#111] border-white/15" /></Field>
                <Field label="Last name *"><Input data-testid="input-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-[#111] border-white/15" /></Field>
              </div>
              <Field label="Email *"><Input data-testid="input-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#111] border-white/15" /></Field>
              <Field label="Phone *"><Input data-testid="input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#111] border-white/15" /></Field>
              <Field label="Birthday (optional)"><Input data-testid="input-dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="bg-[#111] border-white/15 [color-scheme:dark]" /></Field>
              <Field label="Project details (optional)"><Textarea data-testid="input-details" value={form.project_details} onChange={(e) => setForm({ ...form, project_details: e.target.value })} className="bg-[#111] border-white/15" rows={3} /></Field>

              <label htmlFor="sms-consent" className="w-full flex items-start gap-3 rounded-xl p-4 border border-white/10 bg-[#111] hover:border-white/25 transition-colors cursor-pointer">
                <input
                  id="sms-consent"
                  name="sms_consent"
                  type="checkbox"
                  checked={form.sms_consent}
                  onChange={(e) => setForm({ ...form, sms_consent: e.target.checked })}
                  data-testid="sms-consent-checkbox"
                  className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-[#D4AF37]"
                />
                <span className="text-white/60 text-xs leading-relaxed">
                  <span className="text-white/80 font-medium">SMS updates (optional).</span> {SMS_CONSENT_TEXT} Consent is not a condition of booking.
                </span>
              </label>
              <p className="text-white/40 text-[11px] mt-2 px-1">
                By opting in you agree to our{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" data-testid="consent-privacy-link" className="text-[#D4AF37] hover:underline">Privacy Policy</a>{" "}and{" "}
                <a href="/terms" target="_blank" rel="noreferrer" data-testid="consent-terms-link" className="text-[#D4AF37] hover:underline">Terms &amp; Conditions</a>.
              </p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-2" data-testid="step-service">
              <p className="text-white/50 text-xs mb-1">Tap a service to continue</p>
              {services.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { setServiceId(s.id); setStep(2); }}
                  data-testid={`select-service-${s.slug}`}
                  className={`w-full flex items-center gap-3 rounded-xl border p-2.5 transition-colors ${serviceId === s.id ? "border-[#D4AF37] bg-[#161310]" : "border-white/10 bg-[#111] hover:border-white/30"}`}
                >
                  <img src={mediaUrl(s.image)} alt={s.title} className="h-12 w-12 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 text-left min-w-0">
                    <h3 className="font-display text-white text-sm truncate">{s.title}</h3>
                    <p className="text-white/40 text-xs truncate">{s.category}</p>
                  </div>
                  <span className="text-[#D4AF37] text-sm font-semibold shrink-0">{s.hourly_rate > 0 ? `$${s.hourly_rate}/hr` : s.price}</span>
                  <ChevronRight size={16} className="text-white/30 shrink-0" />
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col items-center" data-testid="step-date">
              <div className="glass rounded-2xl p-3">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => {
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    return d < today || blockedDates.has(fmtDate(d)) || closedWeekdays.has(d.getDay());
                  }}
                  className="text-white"
                />
              </div>
              {dateStr && dayInfo && !dayInfo.open && (
                <p className="text-red-400 text-sm mt-4">{dayInfo.blocked_reason || "Studio is closed on this date."}</p>
              )}
              {dateStr && dayInfo?.open && (
                <p className="text-white/60 text-sm mt-4 flex items-center gap-2"><CalendarDays size={15} className="text-[#D4AF37]" /> {date.toDateString()} — open {fmtHour(dayInfo.start)} to {fmtHour(dayInfo.end)}</p>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-time">
              <p className="text-white/70 text-sm mb-3">Choose a start time</p>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-8">
                {availableStarts.map((h) => (
                  <button
                    key={h}
                    onClick={() => { setStartTime(h); setHours(1); }}
                    data-testid={`time-slot-${h}`}
                    className={`py-2 rounded-lg text-sm transition-colors ${startTime === h ? "btn-gold" : "border border-white/15 text-white/80 hover:border-[#D4AF37]/50"}`}
                  >
                    {fmtHour(h)}
                  </button>
                ))}
                {availableStarts.length === 0 && <p className="text-white/40 text-sm col-span-full">No open slots this day.</p>}
              </div>
              {startTime != null && (
                <div className="glass rounded-2xl p-5">
                  <p className="text-white/70 text-sm mb-3 flex items-center gap-2"><Clock size={15} className="text-[#D4AF37]" /> Duration (hours)</p>
                  <div className="flex items-center gap-5">
                    <button onClick={() => setHours((h) => Math.max(1, h - 1))} data-testid="hours-minus" className="h-10 w-10 rounded-full border border-white/20 text-white flex items-center justify-center hover:border-[#D4AF37]"><Minus size={16} /></button>
                    <span className="font-display text-3xl text-white w-10 text-center" data-testid="hours-value">{hours}</span>
                    <button onClick={() => setHours((h) => Math.min(maxHours, h + 1))} data-testid="hours-plus" disabled={hours >= maxHours} className="h-10 w-10 rounded-full border border-white/20 text-white flex items-center justify-center hover:border-[#D4AF37] disabled:opacity-30"><Plus size={16} /></button>
                    <span className="text-white/40 text-xs ml-2">Ends {fmtHour(startTime + hours)} · max {maxHours}h</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-addons">
              <p className="text-white/70 text-sm mb-4">Optional equipment add-ons</p>
              <div className="space-y-3">
                {equip.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => toggleEquip(e.id)}
                    data-testid={`addon-${e.id}`}
                    className={`w-full text-left flex items-center gap-4 rounded-2xl p-4 border transition-colors ${equipment.includes(e.id) ? "border-[#D4AF37] bg-[#161310]" : "border-white/10 bg-[#111] hover:border-white/30"}`}
                  >
                    <span className={`h-5 w-5 rounded border flex items-center justify-center ${equipment.includes(e.id) ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/30"}`}>
                      {equipment.includes(e.id) && <Check size={13} className="text-black" />}
                    </span>
                    <div className="flex-1">
                      <p className="text-white">{e.name}</p>
                      <p className="text-white/40 text-xs">{e.description}</p>
                    </div>
                    <span className="text-[#D4AF37] font-semibold">+{fmtMoney(Math.round(e.price * 100))}</span>
                  </button>
                ))}
                {equip.length === 0 && <p className="text-white/40 text-sm">No add-ons available.</p>}
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="step-payment">
              <div className="glass rounded-2xl p-6 mb-5">
                <h3 className="font-display text-xl text-white mb-4">Order Summary</h3>
                <Row label={service?.title} value={`${hours}h × $${service?.hourly_rate}/hr`} />
                {equipment.map((id) => {
                  const e = equip.find((x) => x.id === id);
                  return e ? <Row key={id} label={e.name} value={fmtMoney(Math.round(e.price * 100))} /> : null;
                })}
                <div className="border-t border-white/10 my-3" />
                <Row label="Subtotal" value={fmtMoney(subtotalCents)} />
                {discountCents > 0 && <Row label={`Promo (${promo.code})`} value={`−${fmtMoney(discountCents)}`} gold />}
                <div className="border-t border-white/10 my-3" />
                <div className="flex justify-between items-center">
                  <span className="font-display text-lg text-white">Total</span>
                  <span className="font-display text-2xl text-[#D4AF37]" data-testid="order-total">{fmtMoney(totalCents)}</span>
                </div>
              </div>

              <div className="flex gap-2 mb-6">
                <Input data-testid="promo-input" placeholder="Promo code" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} className="bg-[#111] border-white/15" />
                <button onClick={applyPromo} data-testid="apply-promo-btn" className="shrink-0 rounded-md px-5 border border-white/20 text-white hover:border-[#D4AF37] flex items-center gap-2 text-sm"><Tag size={15} /> Apply</button>
              </div>

              <button onClick={payWithStripe} disabled={paying} data-testid="pay-stripe-btn" className="btn-gold w-full rounded-full px-6 py-4 uppercase tracking-wide font-mono-label text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                {paying ? <Loader2 className="animate-spin" size={18} /> : null} Pay {fmtMoney(totalCents)} with Card
              </button>
              {payConfig?.paypal_enabled && (
                <>
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-white/40 text-xs uppercase font-mono-label">or</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                  <button
                    onClick={payWithPayPal}
                    disabled={paying}
                    data-testid="pay-paypal-btn"
                    className="w-full rounded-full px-6 py-4 uppercase tracking-wide font-mono-label text-sm flex items-center justify-center gap-2 bg-[#ffc439] text-[#003087] font-bold hover:brightness-105 transition-all disabled:opacity-60"
                  >
                    {paying ? <Loader2 className="animate-spin" size={18} /> : null} Pay {fmtMoney(totalCents)} with PayPal
                  </button>
                </>
              )}
              <p className="text-white/40 text-xs text-center mt-4">Your slot is held as pending until payment completes. Secure checkout via Stripe &amp; PayPal — you'll be redirected to complete payment, then returned here.</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Nav buttons */}
      <div className="flex items-center justify-between mt-10">
        <button onClick={back} disabled={step === 0} data-testid="step-back-btn" className="flex items-center gap-2 text-white/60 hover:text-white disabled:opacity-0 text-sm uppercase font-mono-label tracking-wide">
          <ChevronLeft size={18} /> Back
        </button>
        {step < STEPS.length - 1 && (
          <button onClick={next} disabled={!canNext()} data-testid="step-next-btn" className="btn-gold rounded-full px-8 py-3 uppercase tracking-wide font-mono-label text-sm flex items-center gap-2 disabled:opacity-40">
            Continue <ChevronRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-white/60 text-xs uppercase tracking-wide font-mono-label mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
function Row({ label, value, gold }) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-white/60">{label}</span>
      <span className={gold ? "text-[#D4AF37]" : "text-white/80"}>{value}</span>
    </div>
  );
}
